"use client";

import * as React from "react";
import { LiquidPill } from "components/ui/v2";
import { cn } from "@/lib/utils";
import { ImageIcon, Loader2, Trash2 } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { ImageZoom, type ImageZoomRect } from "components/ui/v2/image-zoom";

export interface ExploreCommentMediaPreviewV2Props {
  imageUrls: string[];
  loading?: boolean;
  pendingCount?: number;
  removingUrl?: string | null;
  onRemove?: (url: string) => void | Promise<void>;
}

function MediaThumbnail({
  src,
  alt,
  onRemove,
  onPreview,
  removing = false,
}: {
  src: string;
  alt: string;
  onRemove?: () => void;
  onPreview?: (url: string, sourceRect: ImageZoomRect) => void;
  removing?: boolean;
}) {
  const { t } = useTranslation("common");
  const [loaded, setLoaded] = React.useState(false);

  return (
    <div
      role={onPreview ? "button" : undefined}
      tabIndex={onPreview ? 0 : undefined}
      onClick={(event) => {
        if (removing || !onPreview) return;
        const rect = event.currentTarget.getBoundingClientRect();
        onPreview(src, {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }}
      onKeyDown={(event) => {
        if (!onPreview || removing) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          onPreview(src, {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        }
      }}
      className={cn(
        "group/thumb relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 shadow-sm dark:border-zinc-700 dark:bg-zinc-800/80",
        onPreview && !removing && "cursor-zoom-in",
        removing && "opacity-70"
      )}
    >
      {!loaded || removing ? (
        <div className="absolute inset-0 z-[1] flex items-center justify-center bg-slate-100/90 dark:bg-zinc-800">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
        </div>
      ) : null}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        className={cn(
          "h-full w-full object-cover transition-opacity duration-200",
          loaded && !removing ? "opacity-100" : "opacity-0"
        )}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
      {onRemove ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          disabled={removing}
          aria-label={t("Eliminar imagen")}
          className="absolute left-1 top-1 z-[2] flex h-6 w-6 items-center justify-center rounded-full bg-black/85 text-white opacity-0 transition-opacity duration-150 hover:bg-black group-hover/thumb:opacity-100 disabled:cursor-not-allowed"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

function MediaThumbnailSkeleton() {
  return (
    <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dashed border-indigo-200/80 bg-indigo-50/60 dark:border-indigo-500/30 dark:bg-indigo-950/20">
      <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
    </div>
  );
}

export function ExploreCommentMediaPreviewV2({
  imageUrls,
  loading = false,
  pendingCount = 0,
  removingUrl = null,
  onRemove,
}: ExploreCommentMediaPreviewV2Props) {
  const { t } = useTranslation("common");
  const [zoomImage, setZoomImage] = React.useState<{
    url: string;
    sourceRect: ImageZoomRect;
  } | null>(null);
  const skeletonCount = loading ? Math.max(pendingCount, 0) : 0;
  const totalItems = imageUrls.length + skeletonCount;

  if (totalItems === 0) return null;

  return (
    <>
    <div className="border-t border-slate-100/80 px-3 py-2 dark:border-zinc-800/60">
      <div className="mb-2 flex items-center gap-2">
        <LiquidPill
          variant="indigo"
          appearance="tinted"
          shadow="neutral"
          size="xs"
          icon={
            loading || removingUrl ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ImageIcon className="h-3 w-3" />
            )
          }
          text={
            removingUrl
              ? t("Eliminando imagen...")
              : loading
                ? skeletonCount === 1
                  ? t("Subiendo imagen...")
                  : t("Subiendo {{count}} imágenes...", { count: skeletonCount })
                : imageUrls.length === 1
                  ? t("1 imagen adjunta")
                  : t("{{count}} imágenes adjuntas", { count: imageUrls.length })
          }
        />
        <span className="text-[10px] text-slate-500 dark:text-zinc-400">
          {removingUrl ? t("Eliminando") : loading ? t("Cargando") : t("Vista previa")}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {imageUrls.map((url) => (
          <MediaThumbnail
            key={url}
            src={url}
            alt={t("Imagen del comentario")}
            removing={removingUrl === url}
            onPreview={(url, sourceRect) => setZoomImage({ url, sourceRect })}
            onRemove={
              onRemove
                ? () => {
                    void onRemove(url);
                  }
                : undefined
            }
          />
        ))}
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <MediaThumbnailSkeleton key={`upload-skeleton-${index}`} />
        ))}
      </div>
    </div>

    <ImageZoom
      open={Boolean(zoomImage)}
      src={zoomImage?.url ?? null}
      sourceRect={zoomImage?.sourceRect ?? null}
      onClose={() => setZoomImage(null)}
      alt={t("Imagen del comentario")}
      closeButtonLabel={t("Cerrar")}
    />
    </>
  );
}
